import React, { useState, useCallback } from 'react'
import { useDropzone } from 'react-dropzone'
import { Box, Typography, Chip, CircularProgress } from '@material-ui/core'
import {
    AttachFile as AttachFileIcon,
    InsertDriveFile as FileIcon,
    PictureAsPdf as PdfIcon,
    Image as ImageIcon,
} from '@material-ui/icons'
import { makeStyles, Theme } from '@material-ui/core/styles'
import { toast } from 'react-toastify'

import { CampaignStepAttachment } from '../../../../models/Outreach/types'
import { useAttachments } from '../../../../hooks/Outreach/useCampaign'

const MAX_FILE_SIZE = 10 * 1024 * 1024 // 10 MB

const useStyles = makeStyles((theme: Theme) => ({
    dropzone: {
        border: '2px dashed #d1d5db',
        borderRadius: 8,
        padding: theme.spacing(2),
        textAlign: 'center',
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        backgroundColor: '#fafafa',
        '&:hover': {
            borderColor: '#3b82f6',
            backgroundColor: '#eff6ff',
        },
    },
    dropzoneActive: {
        borderColor: '#3b82f6',
        backgroundColor: '#eff6ff',
    },
    attachmentList: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: theme.spacing(1),
        marginTop: theme.spacing(1),
    },
    attachmentChip: {
        maxWidth: 240,
        '& .MuiChip-label': {
            overflow: 'hidden',
            textOverflow: 'ellipsis',
        },
    },
    uploadingItem: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        padding: theme.spacing(0.5, 1),
        backgroundColor: '#f8fafc',
        borderRadius: 4,
        fontSize: '0.8rem',
    },
}))

const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

const getFileIcon = (fileType: string) => {
    if (fileType.startsWith('image/')) return <ImageIcon fontSize="small" />
    if (fileType === 'application/pdf') return <PdfIcon fontSize="small" />
    return <FileIcon fontSize="small" />
}

interface LocalModeProps {
    mode: 'local'
    pendingFiles: File[]
    onPendingFilesChange: (files: File[]) => void
    campaignId?: undefined
    stepId?: undefined
    attachments?: undefined
    onAttachmentsChange?: undefined
}

interface ApiModeProps {
    mode: 'api'
    campaignId: number | string
    stepId: number | string
    attachments: CampaignStepAttachment[]
    onAttachmentsChange: (attachments: CampaignStepAttachment[]) => void
    pendingFiles?: undefined
    onPendingFilesChange?: undefined
}

type FileAttachmentsProps = LocalModeProps | ApiModeProps

export const FileAttachments: React.FC<FileAttachmentsProps> = (props) => {
    const classes = useStyles()
    const [uploading, setUploading] = useState<string[]>([])

    const isApiMode = props.mode === 'api'
    const { uploadAttachment, deleteAttachment } = useAttachments(
        isApiMode ? props.campaignId : undefined,
        isApiMode ? props.stepId : undefined,
    )

    const onDrop = useCallback(
        async (acceptedFiles: File[]) => {
            for (const file of acceptedFiles) {
                if (file.size > MAX_FILE_SIZE) {
                    toast.error(`${file.name} exceeds the 10MB size limit`)
                    continue
                }

                if (isApiMode) {
                    const uploadId = `${file.name}-${Date.now()}`
                    setUploading((prev) => [...prev, uploadId])

                    try {
                        const { data } = await uploadAttachment.mutateAsync(
                            file,
                        )
                        props.onAttachmentsChange([...props.attachments, data])
                        toast.success(`${file.name} uploaded`)
                    } catch (err) {
                        // Error toast handled by mutation
                    } finally {
                        setUploading((prev) =>
                            prev.filter((id) => id !== uploadId),
                        )
                    }
                } else {
                    props.onPendingFilesChange([...props.pendingFiles, file])
                }
            }
        },
        [isApiMode, uploadAttachment, props],
    )

    const handleDeleteAttachment = useCallback(
        async (attachmentId: number) => {
            if (!isApiMode) return
            try {
                await deleteAttachment.mutateAsync(attachmentId)
                props.onAttachmentsChange(
                    props.attachments.filter((a) => a.id !== attachmentId),
                )
            } catch (err) {
                // Error toast handled by mutation
            }
        },
        [isApiMode, deleteAttachment, props],
    )

    const handleRemovePendingFile = useCallback(
        (index: number) => {
            if (isApiMode) return
            const updated = [...props.pendingFiles]
            updated.splice(index, 1)
            props.onPendingFilesChange(updated)
        },
        [isApiMode, props],
    )

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        maxSize: MAX_FILE_SIZE,
        onDropRejected: (rejections) => {
            rejections.forEach((rejection) => {
                rejection.errors.forEach((error) => {
                    if (error.code === 'file-too-large') {
                        toast.error(
                            `${rejection.file.name} exceeds the 10MB size limit`,
                        )
                    } else {
                        toast.error(`${rejection.file.name}: ${error.message}`)
                    }
                })
            })
        },
    })

    const hasItems =
        (isApiMode && props.attachments.length > 0) ||
        (!isApiMode && props.pendingFiles.length > 0) ||
        uploading.length > 0

    return (
        <Box>
            {/* Server-side attachments (API mode) */}
            {isApiMode && props.attachments.length > 0 && (
                <Box className={classes.attachmentList}>
                    {props.attachments.map((attachment) => (
                        <Chip
                            key={attachment.id}
                            icon={getFileIcon(attachment.file_type)}
                            label={`${attachment.file_name} (${formatFileSize(
                                attachment.file_size,
                            )})`}
                            onDelete={() =>
                                handleDeleteAttachment(attachment.id)
                            }
                            variant="outlined"
                            size="small"
                            className={classes.attachmentChip}
                        />
                    ))}
                </Box>
            )}

            {/* Pending local files (local mode) */}
            {!isApiMode && props.pendingFiles.length > 0 && (
                <Box className={classes.attachmentList}>
                    {props.pendingFiles.map((file, index) => (
                        <Chip
                            key={`${file.name}-${index}`}
                            icon={getFileIcon(file.type)}
                            label={`${file.name} (${formatFileSize(
                                file.size,
                            )})`}
                            onDelete={() => handleRemovePendingFile(index)}
                            variant="outlined"
                            size="small"
                            className={classes.attachmentChip}
                        />
                    ))}
                </Box>
            )}

            {uploading.length > 0 && (
                <Box className={classes.attachmentList}>
                    {uploading.map((id) => (
                        <Box key={id} className={classes.uploadingItem}>
                            <CircularProgress size={14} />
                            <Typography variant="caption">
                                Uploading...
                            </Typography>
                        </Box>
                    ))}
                </Box>
            )}

            <Box
                {...getRootProps()}
                className={`${classes.dropzone} ${
                    isDragActive ? classes.dropzoneActive : ''
                }`}
                style={{ marginTop: hasItems ? 8 : 0 }}
            >
                <input {...getInputProps()} />
                <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    style={{ gap: 8 }}
                >
                    <AttachFileIcon
                        style={{ fontSize: 18, color: '#9ca3af' }}
                    />
                    <Typography variant="caption" color="textSecondary">
                        {isDragActive
                            ? 'Drop files here...'
                            : 'Drag & drop files here, or click to browse (max 10MB)'}
                    </Typography>
                </Box>
            </Box>
        </Box>
    )
}

export default FileAttachments
